import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { GenericToolbar, type ToolbarViewMode } from '@/components/ui/generic-toolbar/GenericToolbar';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { TableColumnHeader } from '@/components/ui/table-cells/TableColumnHeader';
import { useDebounce } from '@/hooks/use-debounce';
import { useIsMobile } from '@/hooks/use-mobile';
import { useServerFilters } from '@/hooks/use-server-filters';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type PageProps, type PaginationMeta } from '@/types';
import { Head, Link, usePage } from '@inertiajs/react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

interface ActivityLogItem {
    id: string;
    log_name: string;
    event: string | null;
    description: string;
    subject_type: string | null;
    subject_id: string | null;
    subject_url: string | null;
    causer: string | null;
    properties: Record<string, unknown> | null;
    created_at: string | null;
}

interface ActivityLogPageProps extends PageProps {
    activities: ActivityLogItem[];
    pagination: PaginationMeta;
    filters: Record<string, string | number | undefined>;
    filter_options: {
        log_names: string[];
        events: string[];
        causers: Array<{ id: string; name: string }>;
    };
    [key: string]: unknown;
}

const logNameBadgeClassMap: Record<string, string> = {
    bug_report: 'bg-cat-purple text-cat-purple-foreground border-cat-purple-foreground/30',
    work_order: 'bg-cat-teal text-cat-teal-foreground border-cat-teal-foreground/30',
    purchase_order: 'bg-cat-indigo text-cat-indigo-foreground border-cat-indigo-foreground/30',
    service_request: 'bg-cat-cyan text-cat-cyan-foreground border-cat-cyan-foreground/30',
    default: 'bg-cat-gray text-cat-gray-foreground border-cat-gray-foreground/30',
};

const eventBadgeClassMap: Record<string, string> = {
    created: 'bg-success-subtle text-success-subtle-foreground border-transparent',
    updated: 'bg-info-subtle text-info-subtle-foreground border-transparent',
    deleted: 'bg-danger-subtle text-danger-subtle-foreground border-transparent',
};

type FilterColumn = 'log_name' | 'event' | 'causer';

const filterBackendKey: Record<FilterColumn, string> = {
    log_name: 'log_name',
    event: 'event',
    causer: 'causer_id',
};

const columnToBackendSort: Record<string, string> = {
    created: 'created_at',
    log_name: 'log_name',
    event: 'event',
};

const backendSortToColumn: Record<string, string> = {
    created_at: 'created',
    log_name: 'log_name',
    event: 'event',
};

// Columns the backend can order by (see Admin\ActivityLogController).
const SORTABLE_COLUMNS = new Set(['created', 'log_name', 'event']);

const humanize = (value: string): string => value.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());

export default function ActivityLogIndexPage() {
    const { t } = useTranslations();
    const { props } = usePage<ActivityLogPageProps>();
    const { activities, pagination, filters, filter_options: filterOptions } = props;
    const { applyFilters, loading } = useServerFilters(filters, '/admin/activity-log');
    const isMobile = useIsMobile();

    const [viewMode, setViewMode] = useState<ToolbarViewMode>(isMobile ? 'grid' : 'table');
    const [search, setSearch] = useState(String(filters.search ?? ''));
    const debouncedSearch = useDebounce(search, 800);
    const [columnFilterSearch, setColumnFilterSearch] = useState<Record<string, string>>({});
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set(['created', 'log_name', 'event', 'description', 'subject', 'causer']));

    const toArray = (value: string | number | undefined): string[] => {
        if (value === undefined || value === null || String(value) === '') return [];
        return [String(value)];
    };

    const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(() => ({
        log_name: toArray(filters.log_name),
        event: toArray(filters.event),
        causer: toArray(filters.causer_id),
    }));

    const [dateFrom, setDateFrom] = useState(String(filters.created_from ?? ''));
    const [dateTo, setDateTo] = useState(String(filters.created_to ?? ''));

    const sortColumn = String(filters.sort ?? 'created_at');
    const sortDirection = (filters.direction === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc';
    const tableSortColumn = backendSortToColumn[sortColumn] ?? null;
    const selectedPerPage = Number(filters.per_page ?? pagination.per_page ?? 25);

    useEffect(() => {
        setViewMode((current) => (isMobile && current === 'table' ? 'grid' : current));
    }, [isMobile]);

    useEffect(() => {
        const currentSearch = String(filters.search ?? '');
        if (currentSearch === debouncedSearch) return;
        applyFilters({ search: debouncedSearch || undefined, page: 1 });
    }, [applyFilters, debouncedSearch, filters.search]);

    useEffect(() => {
        setSearch(String(filters.search ?? ''));
        setColumnFilters({
            log_name: toArray(filters.log_name),
            event: toArray(filters.event),
            causer: toArray(filters.causer_id),
        });
        setDateFrom(String(filters.created_from ?? ''));
        setDateTo(String(filters.created_to ?? ''));
    }, [filters.search, filters.log_name, filters.event, filters.causer_id, filters.created_from, filters.created_to]);

    const quickFilterDefinitions = [
        { id: 'log_name', label: t('activity_log.filters.log_type', 'Log type') },
        { id: 'event', label: t('activity_log.filters.event', 'Event') },
        { id: 'causer', label: t('activity_log.filters.user', 'User') },
    ];

    const columnDefinitions = [
        { id: 'created', label: t('activity_log.table.timestamp', 'When') },
        { id: 'log_name', label: t('activity_log.table.log_type', 'Log type') },
        { id: 'event', label: t('activity_log.table.event', 'Event') },
        { id: 'description', label: t('activity_log.table.description', 'Description') },
        { id: 'subject', label: t('activity_log.table.subject', 'Subject') },
        { id: 'causer', label: t('activity_log.table.user', 'User') },
    ];

    const columnFilterOptions = useMemo<Record<string, Array<{ value: string; label: string }>>>(
        () => ({
            log_name: filterOptions.log_names.map((value) => ({ value, label: humanize(value) })),
            event: filterOptions.events.map((value) => ({ value, label: humanize(value) })),
            causer: filterOptions.causers.map((causer) => ({ value: causer.id, label: causer.name })),
        }),
        [filterOptions],
    );

    const handleFilterChange = (column: string, values: string[]) => {
        setColumnFilters((prev) => ({ ...prev, [column]: values }));
        const backendKey = filterBackendKey[column as FilterColumn];
        if (backendKey) {
            applyFilters({ [backendKey]: values[0] || undefined, page: 1 });
        }
    };

    // Single-value filters: selecting an option replaces the previous one.
    const toggleSingleFilter = (column: string, value: string) => {
        const current = columnFilters[column] || [];
        const nextValues = current.includes(value) ? [] : [value];
        handleFilterChange(column, nextValues);
    };

    const clearFilters = () => {
        setColumnFilters({ log_name: [], event: [], causer: [] });
        setSearch('');
        setDateFrom('');
        setDateTo('');
        applyFilters({
            log_name: undefined,
            event: undefined,
            causer_id: undefined,
            created_from: undefined,
            created_to: undefined,
            search: undefined,
            page: 1,
        });
    };

    const quickFilterNames = quickFilterDefinitions.filter((q) => (columnFilters[q.id] || []).length > 0).map((q) => q.label);
    const quickFilterButtonLabel =
        quickFilterNames.length > 0
            ? `${t('activity_log.filters.quick_filters', 'Quick filters')}: ${quickFilterNames.join(', ')}`
            : t('activity_log.filters.all_logs', 'All activity');

    const sortableColumns = [
        { id: 'created_at', label: t('activity_log.sort.created_at', 'Date') },
        { id: 'log_name', label: t('activity_log.sort.log_name', 'Log type') },
        { id: 'event', label: t('activity_log.sort.event', 'Event') },
    ];

    const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
        applyFilters({ sort: column, direction, page: 1 });
    };

    const handleTableSortChange = (column: string, direction: 'asc' | 'desc') => {
        applyFilters({ sort: columnToBackendSort[column] ?? 'created_at', direction, page: 1 });
    };

    const clearSort = () => {
        applyFilters({ sort: 'created_at', direction: 'desc', page: 1 });
    };

    const handleColumnShow = (column: string) => {
        setVisibleColumns((prev) => new Set(prev).add(column));
    };

    const handleColumnHide = (column: string) => {
        setVisibleColumns((prev) => {
            const next = new Set(prev);
            next.delete(column);
            return next;
        });
    };

    const handleDateFromChange = (value: string) => {
        setDateFrom(value);
        applyFilters({ created_from: value || undefined, page: 1 });
    };

    const handleDateToChange = (value: string) => {
        setDateTo(value);
        applyFilters({ created_to: value || undefined, page: 1 });
    };

    const handleResetDates = () => {
        setDateFrom('');
        setDateTo('');
        applyFilters({ created_from: undefined, created_to: undefined, page: 1 });
    };

    const logNameBadge = (logName: string) => (
        <Badge variant="outline" className={logNameBadgeClassMap[logName] ?? logNameBadgeClassMap.default}>
            {humanize(logName)}
        </Badge>
    );

    const eventBadge = (event: string | null): ReactNode => {
        if (!event) return <span className="text-muted-foreground text-sm">—</span>;
        return (
            <Badge variant="outline" className={eventBadgeClassMap[event] ?? 'bg-muted text-foreground border-border'}>
                {humanize(event)}
            </Badge>
        );
    };

    const subjectCell = (item: ActivityLogItem): ReactNode => {
        if (!item.subject_type) return <span className="text-muted-foreground text-sm">{t('activity_log.table.no_subject', '—')}</span>;
        const label = `${item.subject_type}${item.subject_id ? ` #${item.subject_id.slice(0, 8)}` : ''}`;
        return item.subject_url ? (
            <Link href={item.subject_url} className="text-primary hover:underline">
                {label}
            </Link>
        ) : (
            <span>{label}</span>
        );
    };

    const causerCell = (item: ActivityLogItem): ReactNode =>
        item.causer ?? <span className="text-muted-foreground text-sm">{t('activity_log.table.system', 'System')}</span>;

    const renderCell: Record<string, (item: ActivityLogItem) => ReactNode> = {
        created: (item) => (
            <span className="text-muted-foreground text-sm whitespace-nowrap">
                {item.created_at ? new Date(item.created_at).toLocaleString() : '—'}
            </span>
        ),
        log_name: (item) => logNameBadge(item.log_name),
        event: (item) => eventBadge(item.event),
        description: (item) => (
            <span className="block max-w-[360px] truncate" title={item.properties ? JSON.stringify(item.properties, null, 2) : undefined}>
                {item.description}
            </span>
        ),
        subject: (item) => subjectCell(item),
        causer: (item) => causerCell(item),
    };

    const orderedColumns = columnDefinitions.filter((column) => visibleColumns.has(column.id));

    const breadcrumbs = [{ title: t('activity_log.title', 'Audit Log'), href: '/admin/activity-log' }];

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('activity_log.title', 'Audit Log')} />

            <PageHeader
                title={t('activity_log.title', 'Audit Log')}
                subtitle={t('activity_log.subtitle', 'Review every recorded change across the system.')}
            />

            <PageContent>
                <GenericToolbar
                    quickFilterButtonLabel={quickFilterButtonLabel}
                    quickFilterDefinitions={quickFilterDefinitions}
                    columnDefinitions={columnDefinitions}
                    columnFilterOptions={columnFilterOptions}
                    columnFilters={columnFilters}
                    columnFilterSearch={columnFilterSearch}
                    setColumnFilterSearch={setColumnFilterSearch}
                    toggleSingleFilter={toggleSingleFilter}
                    handleFilterChange={handleFilterChange}
                    clearFilters={clearFilters}
                    isFilterOpen={isFilterOpen}
                    setIsFilterOpen={setIsFilterOpen}
                    isSortOpen={isSortOpen}
                    setIsSortOpen={setIsSortOpen}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    handleSortChange={handleSortChange}
                    clearSort={clearSort}
                    search={search}
                    setSearch={setSearch}
                    perPage={selectedPerPage}
                    onPerPageChange={(perPage) => applyFilters({ per_page: perPage, page: 1 })}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    visibleColumns={visibleColumns}
                    handleColumnShow={handleColumnShow}
                    handleColumnHide={handleColumnHide}
                    filteredCount={pagination.total}
                    resourceName="activity_log"
                    resourceNamePlural={t('activity_log.reports_plural', 'entries')}
                    sortableColumns={sortableColumns}
                    excludeFromMoreFilters={['log_name', 'event', 'causer']}
                    customFilters={
                        <div className="space-y-3">
                            <div className="flex items-center justify-between text-sm font-medium">
                                <span>{t('activity_log.filters.date_range', 'Date range')}</span>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground h-auto px-2 py-1 text-xs"
                                    disabled={!dateFrom && !dateTo}
                                    onClick={handleResetDates}
                                >
                                    {t('common.reset', 'Reset')}
                                </Button>
                            </div>
                            <div className="flex flex-col gap-2">
                                <div className="grid gap-1">
                                    <label className="text-muted-foreground text-xs">{t('activity_log.filters.from_date', 'From date')}</label>
                                    <DatePicker
                                        value={dateFrom}
                                        onValueChange={handleDateFromChange}
                                        placeholder={t('common.pick_date', 'Pick a date')}
                                    />
                                </div>
                                <div className="grid gap-1">
                                    <label className="text-muted-foreground text-xs">{t('activity_log.filters.to_date', 'To date')}</label>
                                    <DatePicker
                                        value={dateTo}
                                        onValueChange={handleDateToChange}
                                        placeholder={t('common.pick_date', 'Pick a date')}
                                    />
                                </div>
                            </div>
                        </div>
                    }
                />

                {viewMode === 'grid' ? (
                    activities.length === 0 ? (
                        <Card>
                            <CardContent className="py-12 text-center">
                                <p className="text-muted-foreground">{t('activity_log.table.empty', 'No log entries found.')}</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {activities.map((item) => (
                                <div key={item.id} className="bg-card flex flex-col gap-3 rounded-xl border p-5">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        {logNameBadge(item.log_name)}
                                        {eventBadge(item.event)}
                                    </div>
                                    <p className="line-clamp-3 text-sm">{item.description}</p>
                                    <div className="text-muted-foreground flex items-center justify-between text-xs">
                                        <span>{subjectCell(item)}</span>
                                        <span>{item.created_at ? new Date(item.created_at).toLocaleString() : '—'}</span>
                                    </div>
                                    <div className="text-muted-foreground border-t pt-2 text-xs">{causerCell(item)}</div>
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    <div className="bg-card overflow-hidden rounded-xl">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {orderedColumns.map((column) => {
                                        const selectedFilters = columnFilters[column.id] || [];
                                        const filterOptionsForColumn = columnFilterOptions[column.id] || [];
                                        const sortable = SORTABLE_COLUMNS.has(column.id);

                                        return (
                                            <TableHead key={column.id}>
                                                <TableColumnHeader
                                                    label={column.label}
                                                    sortDirection={tableSortColumn === column.id ? sortDirection : null}
                                                    onSortAscending={sortable ? () => handleTableSortChange(column.id, 'asc') : undefined}
                                                    onSortDescending={sortable ? () => handleTableSortChange(column.id, 'desc') : undefined}
                                                    onHide={() => handleColumnHide(column.id)}
                                                    filterOptions={filterOptionsForColumn}
                                                    selectedFilters={selectedFilters}
                                                    onFilterChange={(values) => handleFilterChange(column.id, values)}
                                                    isFiltered={selectedFilters.length > 0}
                                                />
                                            </TableHead>
                                        );
                                    })}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {activities.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={orderedColumns.length} className="text-muted-foreground py-12 text-center">
                                            {t('activity_log.table.empty', 'No log entries found.')}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    activities.map((item) => (
                                        <TableRow key={item.id}>
                                            {orderedColumns.map((column) => (
                                                <TableCell key={column.id}>{renderCell[column.id](item)}</TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}

                <PaginationControls
                    pagination={pagination}
                    loading={loading}
                    onPageChange={(page) => applyFilters({ page })}
                    onPerPageChange={(perPage) => applyFilters({ per_page: perPage, page: 1 })}
                    className="mt-6"
                />
            </PageContent>
        </AppLayout>
    );
}
